import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
  LabelList, Cell,
} from "recharts";
import {
  Loader2, Download, RefreshCw, AlertTriangle, Scale, Target, FileText, History,
  Save, Users, ClipboardList, Flame, Archive, GitCompare, FileDown,
} from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import CommitteeDetail from "@/components/election/CommitteeDetail";
import PersonCard from "@/components/election/PersonCard";
import DeepAnalysis from "@/components/election/DeepAnalysis";

const PRED_LABEL = {
  KESIN: "Kesin bizden", BELKI: "Belki", BELIRSIZ: "Belirsiz", GELMEYECEK: "Gelmeyecek",
  ULASILAMADI: "Ulaşılamadı", KARSI: "Karşı", "": "Boş",
};
const MATCH_STYLE = {
  UYUM: ["Uyumlu", "bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300"],
  SAPMA: ["Sapma", "bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-300"],
  BEKLEYEN: ["Gelmedi", "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300"],
  ISARETSIZ: ["Eğilim yok", "bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300"],
};
const FILTERS = [
  ["all", "Tümü"], ["marked", "Oy kullananlar"], ["pending", "Bekleyenler"],
  ["sapma", "Sapmalar"], ["suspicious", "Şüpheli sapma"], ["power", "Oy gücü 2+"],
];

const Stat = ({ label, value, sub, tone = "default", testid }) => {
  const tones = {
    default: "text-foreground", good: "text-emerald-600 dark:text-emerald-400",
    bad: "text-red-600 dark:text-red-400", warn: "text-amber-600 dark:text-amber-400",
  };
  return (
    <Card className="p-4" data-testid={testid}>
      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className={`mt-1 font-display text-2xl font-bold ${tones[tone]}`}>{value}</p>
      {sub && <p className="mt-0.5 text-xs text-muted-foreground">{sub}</p>}
    </Card>
  );
};

export default function ReconcileReport() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [noteFor, setNoteFor] = useState(null);
  const [noteDraft, setNoteDraft] = useState({ reason: "", note: "" });
  const [history, setHistory] = useState(null);
  const [karne, setKarne] = useState("all");
  const [archives, setArchives] = useState([]);
  const [archLabel, setArchLabel] = useState("2026 Seçimi");
  const [compare, setCompare] = useState(null);
  const [lossCno, setLossCno] = useState("all");
  const [scope, setScope] = useState("all");
  const [coms, setComs] = useState([]);
  const [person, setPerson] = useState(null);

  const load = async (cnoScope = scope) => {
    setLoading(true);
    try {
      const { data } = await api.get("/election-day/reconcile", {
        params: cnoScope === "all" ? {} : { cno: Number(cnoScope) },
      });
      setData(data);
      if (cnoScope === "all") setComs(data.committees);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const changeScope = async (v) => {
    setScope(v);
    setLossCno(v === "all" ? "all" : v);
    await load(v);
  };

  const sp = (extra = {}) => (scope === "all" ? extra : { cno: Number(scope), ...extra });

  const loadArchives = async () => {
    try {
      const { data } = await api.get("/election-day/archives");
      setArchives(data.rows);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, e));
    }
  };
  useEffect(() => { loadArchives(); }, []);

  const createArchive = async () => {
    if (!window.confirm("Bugünkü muhakeme verisinin tamamı dondurulup salt okunur arşive alınacak. Devam?")) return;
    setBusy(true);
    try {
      await api.post("/election-day/archive", { label: archLabel });
      toast.success("Arşiv oluşturuldu — veriler donduruldu");
      await loadArchives();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, e));
    } finally {
      setBusy(false);
    }
  };

  const download = async (scopeName, format) => {
    setBusy(true);
    try {
      const res = await api.get("/election-day/reconcile/export", {
        params: sp({ scope: scopeName, format }), responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `muhakeme_${scopeName}${scope === "all" ? "" : "_K" + scope}.${format === "pdf" ? "pdf" : "xlsx"}`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch {
      toast.error("İndirilemedi");
    } finally {
      setBusy(false);
    }
  };

  const downloadUrl = async (path, params, filename) => {
    setBusy(true);
    try {
      const res = await api.get(path, { params, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch {
      toast.error("İndirilemedi");
    } finally {
      setBusy(false);
    }
  };

  const loadHistory = async () => {
    try {
      const { data } = await api.get("/election-day/reconcile/history", { params: { limit: 500 } });
      setHistory(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, e));
    }
  };

  const openCompare = async (aid) => {
    setBusy(true);
    try {
      const { data } = await api.get(`/election-day/archives/compare/${aid}`);
      setCompare(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, e));
    } finally {
      setBusy(false);
    }
  };

  const openNote = (r) => {
    setNoteFor(r);
    setNoteDraft({ reason: r.reason || "", note: r.note || "" });
  };

  const saveNote = async () => {
    setBusy(true);
    try {
      await api.put("/election-day/reconcile/note", {
        voter_id: noteFor.id, reason: noteDraft.reason, note: noteDraft.note,
      });
      toast.success("Not kaydedildi — değişiklik tarihçeye işlendi");
      setNoteFor(null);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, e));
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return <div className="flex items-center gap-2 p-8 text-muted-foreground" data-testid="reconcile-loading">
      <Loader2 className="h-4 w-4 animate-spin" /> Muhakeme raporu hazırlanıyor...
    </div>;
  }
  if (!data) return null;

  const s = data.summary;
  const rows = data.rows.filter((r) => {
    if (filter === "marked" && !r.marked) return false;
    if (filter === "pending" && r.marked) return false;
    if (filter === "sapma" && r.match !== "SAPMA") return false;
    if (filter === "suspicious" && !r.suspicious) return false;
    if (filter === "power" && (r.vote_power || 0) < 2) return false;
    if (q) {
      const t = `${r.company_name} ${r.sicil_no} ${r.voting_person}`.toLowerCase();
      if (!t.includes(q.toLowerCase())) return false;
    }
    return true;
  });

  const chart = data.committees.map((c) => ({
    k: `K${c.committee_no}`, name: c.name,
    tahmin: c.forecast_biz, kotu: c.worst_biz, cetele: c.tally_biz,
    sandik: c.has_ballot ? c.ballot_our : null, skor: c.score_forecast,
  }));
  const scoreRank = [...data.committees]
    .filter((c) => c.actual_biz > 0)
    .sort((a, b) => b.score_forecast - a.score_forecast)
    .map((c) => ({ k: `K${c.committee_no}`, skor: c.score_forecast, name: c.name }));
  const hm = data.heatmap;

  return (
    <div className="space-y-5" data-testid="reconcile-report">
      {person && <PersonCard name={person} onClose={() => setPerson(null)} />}
      <div className="flex flex-wrap items-center gap-2">
        <span className="grid h-10 w-10 place-items-center rounded-xl bg-[#0F2C59]/10 text-[#0F2C59] dark:text-sky-300">
          <Scale className="h-5 w-5" />
        </span>
        <div className="min-w-0 flex-1">
          <h2 className="font-display text-lg font-bold md:text-lg">Muhakeme & Tutarlılık Raporu</h2>
          <p className="text-xs text-muted-foreground">
            Firma bazlı çetele–tahmin karşılaştırması, sandık sonucu ile tutarlılık skoru
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={() => load()} data-testid="reconcile-refresh">
          <RefreshCw className="mr-1.5 h-4 w-4" /> Yenile
        </Button>
      </div>

      <Card className="border-2 border-[#0F2C59] p-4" data-testid="reconcile-scope">
        <h3 className="text-sm font-bold">Rapor kapsamı — komite seç</h3>
        <p className="mt-0.5 text-xs text-muted-foreground">
          Komite seçtiğinde ekrandaki tüm skorlar, tablolar, grafikler, ısı haritası ve
          <b> indirilen PDF/Excel çıktılarının tamamı</b> yalnız o komiteye göre hazırlanır.
        </p>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <select value={scope} onChange={(e) => changeScope(e.target.value)}
            data-testid="scope-select"
            className="h-9 min-w-[260px] rounded-md border border-border bg-background px-2 text-sm font-semibold">
            <option value="all">Tüm komiteler (genel rapor)</option>
            {(coms.length ? coms : data.committees).map((c) => (
              <option key={c.committee_no} value={c.committee_no}>
                K{c.committee_no} · {c.name}
              </option>
            ))}
          </select>
          {scope !== "all" && (
            <>
              <span className="rounded-full bg-[#0F2C59] px-3 py-1.5 text-xs font-bold text-white"
                data-testid="scope-badge">
                Aktif kapsam: K{scope} · {data.committees[0]?.name} · {data.rows.length} firma
              </span>
              <Button size="sm" variant="ghost" onClick={() => changeScope("all")}
                data-testid="scope-reset">Tümüne dön</Button>
            </>
          )}
        </div>
      </Card>

      {scope !== "all" && <CommitteeDetail data={data} cno={scope} />}

      <DeepAnalysis cno={scope} />

      <Card className="border-2 border-[#0F2C59] p-4" data-testid="reconcile-full-export">
        <h3 className="flex items-center gap-1.5 text-sm font-bold">
          <FileText className="h-4 w-4" /> TÜM DETAY RAPORU
        </h3>
        <p className="mt-0.5 text-xs text-muted-foreground">
          Excel: özet · komite tutarlılık · firma bazlı (notlar dahil) · kişi bazlı · şüpheli sapmalar ·
          sandık sonuçları · sapma notları · değişiklik tarihçesi · oy kullananlar / bekleyenler.
          PDF: kapak + özet + komite tablosu + 48 komite karnesi + şüpheli sapmalar + notlar + tarihçe.
        </p>
        <div className="mt-3 flex flex-wrap gap-2">
          <Button size="sm" disabled={busy} data-testid="export-full-xlsx"
            onClick={() => downloadUrl("/election-day/reconcile/full", sp({ format: "xlsx" }),
              `MUHAKEME_TAM_RAPOR${scope === "all" ? "" : "_K" + scope}.xlsx`)}>
            <Download className="mr-1.5 h-4 w-4" /> Tam Rapor (Excel)
          </Button>
          <Button size="sm" variant="outline" disabled={busy} data-testid="export-full-pdf"
            onClick={() => downloadUrl("/election-day/reconcile/full", sp({ format: "pdf" }),
              `MUHAKEME_TAM_RAPOR${scope === "all" ? "" : "_K" + scope}.pdf`)}>
            <Download className="mr-1.5 h-4 w-4" /> Tam Rapor (PDF)
          </Button>
          <div className="flex items-center gap-1.5">
            <select value={karne} onChange={(e) => setKarne(e.target.value)}
              data-testid="karne-select"
              className="h-9 rounded-md border border-border bg-background px-2 text-xs">
              <option value="all">Tüm komiteler (48 sayfa)</option>
              {(coms.length ? coms : data.committees).map((c) => (
                <option key={c.committee_no} value={c.committee_no}>
                  K{c.committee_no} · {c.name}
                </option>
              ))}
            </select>
            <Button size="sm" variant="outline" disabled={busy} data-testid="export-karne-pdf"
              onClick={() => downloadUrl("/election-day/reconcile/scorecards",
                karne === "all" ? {} : { cno: Number(karne) },
                `komite_karnesi_${karne}.pdf`)}>
              <ClipboardList className="mr-1.5 h-4 w-4" /> Komite Karnesi (PDF)
            </Button>
          </div>
        </div>
      </Card>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Stat testid="stat-score" label="Tutarlılık skoru (tahmin)" value={`%${s.score_forecast}`}
          sub={`${s.grade} · ${s.ballot_committees}/${s.committees} komitede sandık sonucu girili`}
          tone={s.score_forecast >= 75 ? "good" : s.score_forecast >= 60 ? "warn" : "bad"} />
        <Stat testid="stat-bias" label="Tahmin yanlılığı" value={`${s.bias > 0 ? "+" : ""}${s.bias} oy`}
          sub={`${s.bias > 0 ? "Fazla" : "Eksik"} tahmin · %${Math.abs(s.bias_pct)}`}
          tone={Math.abs(s.bias_pct) <= 10 ? "good" : "warn"} />
        <Stat testid="stat-match" label="Firma bazlı uyum" value={`%${s.match_rate}`}
          sub={`${s.uyum} uyumlu · ${s.sapma} sapma`} tone={s.match_rate >= 85 ? "good" : "warn"} />
        <Stat testid="stat-suspicious" label="Şüpheli sapma" value={s.suspicious}
          sub={`Bizden sayıp gelmeyen: ${s.kesin_gelmeyen} firma`} tone="bad" />
      </div>

      <Card className="p-4" data-testid="reconcile-compare-chart">
        <h3 className="text-sm font-bold">Komite bazında karşılaştırma — tahmin / kötü senaryo / çetele / sandık</h3>
        <p className="mt-0.5 text-xs text-muted-foreground">
          Siyah barlar girilmiş resmi sandık sonucudur; yoksa o komitede sandık verisi henüz girilmemiştir.
        </p>
        <div className="mt-3" style={{ height: Math.max(360, chart.length * 26) }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chart} layout="vertical" margin={{ left: 8, right: 24, top: 4, bottom: 4 }}>
              <CartesianGrid strokeDasharray="3 3" horizontal={false} />
              <XAxis type="number" tick={{ fontSize: 11 }} />
              <YAxis type="category" dataKey="k" width={44} tick={{ fontSize: 11 }} />
              <Tooltip formatter={(v, n) => [v, n]}
                labelFormatter={(l) => chart.find((c) => c.k === l)?.name || l} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="tahmin" name="2026 Tahmini" fill="#0F2C59" radius={[0, 3, 3, 0]} />
              <Bar dataKey="kotu" name="Kötü Senaryo" fill="#F59E0B" radius={[0, 3, 3, 0]} />
              <Bar dataKey="cetele" name="Çetele" fill="#16A34A" radius={[0, 3, 3, 0]} />
              <Bar dataKey="sandik" name="Sandık Sonucu" fill="#111827" radius={[0, 3, 3, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Card>

      <Card className="p-4" data-testid="reconcile-score-chart">
        <h3 className="text-sm font-bold">Tutarlılık skoru sıralaması (komite)</h3>
        <p className="mt-0.5 text-xs text-muted-foreground">
          100 = tahmin gerçekleşenle birebir. Yeşil ≥ 85, turuncu 60–85, kırmızı &lt; 60.
        </p>
        <div className="mt-3" style={{ height: Math.max(300, scoreRank.length * 22) }}>
          {!scoreRank.length ? (
            <p className="text-xs text-muted-foreground" data-testid="score-chart-empty">
              Henüz skor hesaplanacak veri yok — komite sandık sonuçları girildiğinde ya da çeteleye
              işaretleme yapıldığında sıralama burada görünür.
            </p>
          ) : (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={scoreRank} layout="vertical" margin={{ left: 8, right: 40, top: 4, bottom: 4 }}>
              <CartesianGrid strokeDasharray="3 3" horizontal={false} />
              <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 11 }} />
              <YAxis type="category" dataKey="k" width={44} tick={{ fontSize: 11 }} />
              <Tooltip labelFormatter={(l) => scoreRank.find((c) => c.k === l)?.name || l}
                formatter={(v) => [`%${v}`, "Tutarlılık"]} />
              <Bar dataKey="skor" radius={[0, 3, 3, 0]}>
                <LabelList dataKey="skor" position="right" fontSize={10} formatter={(v) => `%${v}`} />
                {scoreRank.map((r) => (
                  <Cell key={r.k} fill={r.skor >= 85 ? "#16A34A" : r.skor >= 60 ? "#F59E0B" : "#DC2626"} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
          )}
        </div>
      </Card>

      <Card className="p-4" data-testid="reconcile-committee-table">
        <div className="flex flex-wrap items-center gap-2">
          <h3 className="flex-1 text-sm font-bold">Komite tutarlılık tablosu</h3>
          <Button size="sm" variant="outline" disabled={busy}
            onClick={() => download("committees", "xlsx")} data-testid="export-committees-xlsx">
            <Download className="mr-1.5 h-4 w-4" /> Excel
          </Button>
          <Button size="sm" variant="outline" disabled={busy}
            onClick={() => download("committees", "pdf")} data-testid="export-committees-pdf">
            <Download className="mr-1.5 h-4 w-4" /> PDF
          </Button>
        </div>
        <ScrollArea className="mt-3 h-[380px]">
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-muted">
              <tr className="text-left">
                {["K", "Komite", "FAAL", "Oy", "Kat. %", "Tahmin", "Kötü", "Çetele", "Sandık",
                  "Sapma", "Tutarlılık", "Uyum/Sapma", "Şüpheli"].map((h) => (
                  <th key={h} className="whitespace-nowrap px-2 py-1.5 font-semibold">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.committees.map((c) => (
                <tr key={c.committee_no} className="border-b border-border/60"
                  data-testid={`reconcile-com-${c.committee_no}`}>
                  <td className="px-2 py-1.5 font-bold">{c.committee_no}</td>
                  <td className="max-w-[220px] truncate px-2 py-1.5">{c.name}</td>
                  <td className="px-2 py-1.5">{c.faal}</td>
                  <td className="px-2 py-1.5">{c.marked}</td>
                  <td className="px-2 py-1.5">{c.participation}</td>
                  <td className="px-2 py-1.5 font-semibold text-[#0F2C59] dark:text-sky-300">{c.forecast_biz}</td>
                  <td className="px-2 py-1.5 text-amber-600">{c.worst_biz}</td>
                  <td className="px-2 py-1.5 text-emerald-600">{c.tally_biz}</td>
                  <td className="px-2 py-1.5 font-bold">{c.has_ballot ? c.ballot_our : "—"}</td>
                  <td className={`px-2 py-1.5 font-semibold ${c.dev_forecast > 0 ? "text-red-600" : "text-emerald-600"}`}>
                    {c.dev_forecast > 0 ? "+" : ""}{c.dev_forecast}
                  </td>
                  <td className="px-2 py-1.5 font-bold">%{c.score_forecast}</td>
                  <td className="px-2 py-1.5">{c.uyum} / {c.sapma}</td>
                  <td className="px-2 py-1.5 text-red-600">{c.suspicious}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </ScrollArea>
      </Card>

      <Card className="p-4" data-testid="reconcile-firm-table">
        <div className="flex flex-wrap items-center gap-2">
          <h3 className="flex-1 text-sm font-bold">
            Firma bazlı muhakeme — {rows.length} kayıt
          </h3>
          <Button size="sm" variant="outline" disabled={busy}
            onClick={() => download("firms", "xlsx")} data-testid="export-firms-xlsx">
            <Download className="mr-1.5 h-4 w-4" /> Excel
          </Button>
          <Button size="sm" variant="outline" disabled={busy}
            onClick={() => download("firms", "pdf")} data-testid="export-firms-pdf">
            <Download className="mr-1.5 h-4 w-4" /> PDF
          </Button>
          <Button size="sm" variant="outline" disabled={busy}
            onClick={() => download("suspicious", "xlsx")} data-testid="export-suspicious-xlsx">
            <AlertTriangle className="mr-1.5 h-4 w-4" /> Şüpheli listesi
          </Button>
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          {FILTERS.map(([k, label]) => (
            <button key={k} type="button" onClick={() => setFilter(k)}
              data-testid={`reconcile-filter-${k}`}
              className={`rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${
                filter === k ? "bg-[#0F2C59] text-white" : "bg-muted text-muted-foreground hover:bg-muted/70"}`}>
              {label}
            </button>
          ))}
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Firma / kişi ara"
            data-testid="reconcile-search"
            className="h-8 min-w-[160px] flex-1 rounded-md border border-border bg-background px-2 text-xs" />
        </div>
        <ScrollArea className="mt-3 h-[520px]">
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-muted">
              <tr className="text-left">
                {["Firma", "Komite", "Oy kullanacak kişi", "Oy gücü", "2026 Tahmini",
                  "Çetele durumu", "Çeteledeki eğilim", "Uyum", "İşaretleyen",
                  "Sapma nedeni / Not"].map((h) => (
                  <th key={h} className="whitespace-nowrap px-2 py-1.5 font-semibold">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.slice(0, 1500).map((r) => {
                const [ml, mc] = MATCH_STYLE[r.match] || ["—", ""];
                return (
                  <tr key={r.id} className={`border-b border-border/60 ${r.suspicious ? "bg-red-50 dark:bg-red-950/30" : ""}`}
                    data-testid={`reconcile-row-${r.id}`}>
                    <td className="max-w-[260px] px-2 py-1.5">
                      <span className="block truncate font-medium">{r.company_name}</span>
                      <span className="text-[10px] text-muted-foreground">{r.sicil_no}</span>
                    </td>
                    <td className="whitespace-nowrap px-2 py-1.5">#{r.committee_no}</td>
                    <td className="max-w-[160px] px-2 py-1.5">
                      {r.voting_person ? (
                        <button type="button" onClick={() => setPerson(r.voting_person)}
                          data-testid={`person-open-firm-${r.id}`}
                          className="block max-w-full truncate text-left font-medium text-sky-700 hover:underline dark:text-sky-400">
                          {r.voting_person}
                        </button>
                      ) : "—"}
                    </td>
                    <td className="px-2 py-1.5">
                      {r.vote_power > 1
                        ? <span className="rounded bg-[#0F2C59] px-1.5 py-0.5 font-bold text-white">{r.vote_power}</span>
                        : (r.vote_power || "—")}
                    </td>
                    <td className="whitespace-nowrap px-2 py-1.5">{PRED_LABEL[r.prediction] ?? r.prediction}</td>
                    <td className="whitespace-nowrap px-2 py-1.5">
                      {r.marked
                        ? <span className="font-semibold text-emerald-600">Oy kullandı</span>
                        : <span className="text-muted-foreground">Bekliyor</span>}
                    </td>
                    <td className="whitespace-nowrap px-2 py-1.5">{PRED_LABEL[r.tally] ?? r.tally ?? "—"}</td>
                    <td className="px-2 py-1.5">
                      <span className={`rounded px-1.5 py-0.5 text-[10px] font-bold ${mc}`}>{ml}</span>
                    </td>
                    <td className="whitespace-nowrap px-2 py-1.5 text-muted-foreground">
                      {r.marked_by || "—"} {r.marked_at ? String(r.marked_at).slice(11, 16) : ""}
                    </td>
                    <td className="px-2 py-1.5">
                      <button type="button" onClick={() => openNote(r)}
                        data-testid={`note-edit-${r.id}`}
                        className="max-w-[220px] truncate text-left text-[11px] font-medium text-sky-700 hover:underline dark:text-sky-400">
                        {r.reason || r.note
                          ? `${r.reason}${r.note ? " · " + r.note : ""}`
                          : "+ neden / not ekle"}
                        {r.note_history?.length > 1 && (
                          <span className="ml-1 text-muted-foreground">({r.note_history.length})</span>
                        )}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </ScrollArea>
        {rows.length > 1500 && (
          <p className="mt-2 text-xs text-muted-foreground">
            Ekranda ilk 1500 kayıt gösteriliyor; tamamı için Excel/PDF indirin.
          </p>
        )}
      </Card>

      <Card className="p-4" data-testid="reconcile-persons">
        <div className="flex flex-wrap items-center gap-2">
          <h3 className="flex flex-1 items-center gap-1.5 text-sm font-bold">
            <Users className="h-4 w-4" /> Kişi bazlı muhakeme — {data.persons.length} kişi
          </h3>
          <Button size="sm" variant="outline" disabled={busy}
            onClick={() => download("persons", "xlsx")} data-testid="export-persons-xlsx">
            <Download className="mr-1.5 h-4 w-4" /> Excel
          </Button>
          <Button size="sm" variant="outline" disabled={busy}
            onClick={() => download("persons", "pdf")} data-testid="export-persons-pdf">
            <Download className="mr-1.5 h-4 w-4" /> PDF
          </Button>
        </div>
        <p className="mt-0.5 text-xs text-muted-foreground">
          Oy gücü = kişinin oy kullanabildiği firma sayısı. Gerçekleşme % = tahminde KESİN sayılan
          oylarının kaçı çetelede bize yazıldı.
        </p>
        <ScrollArea className="mt-3 h-[360px]">
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-muted">
              <tr className="text-left">
                {["#", "Kişi", "Oy gücü", "Komite", "Tahmin KESİN", "Oy kullandı", "Gelmedi",
                  "Bize", "Belirsiz", "Karşı", "Sapma", "Şüpheli", "Gerçekleşme %"].map((h) => (
                  <th key={h} className="whitespace-nowrap px-2 py-1.5 font-semibold">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.persons.slice(0, 600).map((p) => (
                <tr key={p.id} className={`border-b border-border/60 ${p.suspicious ? "bg-red-50 dark:bg-red-950/30" : ""}`}
                  data-testid={`person-row-${p.rank}`}>
                  <td className="px-2 py-1.5 font-bold">{p.rank}</td>
                  <td className="max-w-[200px] px-2 py-1.5">
                    <button type="button" onClick={() => setPerson(p.person)}
                      data-testid={`person-open-rank-${p.rank}`}
                      className="block max-w-full truncate text-left font-medium text-sky-700 hover:underline dark:text-sky-400">
                      {p.person}
                    </button>
                  </td>
                  <td className="px-2 py-1.5">
                    <span className={p.power > 1 ? "rounded bg-[#0F2C59] px-1.5 py-0.5 font-bold text-white" : ""}>
                      {p.power}
                    </span>
                  </td>
                  <td className="px-2 py-1.5">{p.committee_count}</td>
                  <td className="px-2 py-1.5">{p.pred_kesin}</td>
                  <td className="px-2 py-1.5">{p.used}</td>
                  <td className="px-2 py-1.5 text-muted-foreground">{p.pending}</td>
                  <td className="px-2 py-1.5 font-semibold text-emerald-600">{p.biz}</td>
                  <td className="px-2 py-1.5">{p.belki}</td>
                  <td className="px-2 py-1.5 text-red-600">{p.karsi}</td>
                  <td className="px-2 py-1.5">{p.sapma}</td>
                  <td className="px-2 py-1.5 font-bold text-red-600">{p.suspicious || ""}</td>
                  <td className="px-2 py-1.5 font-bold">%{p.realized_pct}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </ScrollArea>
      </Card>

      <Card className="p-4" data-testid="reconcile-history">
        <div className="flex flex-wrap items-center gap-2">
          <h3 className="flex flex-1 items-center gap-1.5 text-sm font-bold">
            <History className="h-4 w-4" /> Değişiklik tarihçesi
          </h3>
          <Button size="sm" variant="outline" onClick={loadHistory} data-testid="history-load">
            <RefreshCw className="mr-1.5 h-4 w-4" /> Getir
          </Button>
          <Button size="sm" variant="outline" disabled={busy}
            onClick={() => download("history", "xlsx")} data-testid="export-history-xlsx">
            <Download className="mr-1.5 h-4 w-4" /> Excel
          </Button>
          <Button size="sm" variant="outline" disabled={busy}
            onClick={() => download("history", "pdf")} data-testid="export-history-pdf">
            <Download className="mr-1.5 h-4 w-4" /> PDF
          </Button>
        </div>
        <p className="mt-0.5 text-xs text-muted-foreground">
          Seçim kilidinden (10 Ekim 22:00) sonra yapılan her veri değişikliği: kim · ne zaman ·
          eski değer → yeni değer. Eski seçim verileri bu sayede korunur.
        </p>
        {history && (
          <ScrollArea className="mt-3 h-[300px]" data-testid="history-table">
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-muted">
                <tr className="text-left">
                  {["Tarih / Saat", "Değiştiren", "İşlem", "Kayıt", "Alan", "Eski", "Yeni"].map((h) => (
                    <th key={h} className="whitespace-nowrap px-2 py-1.5 font-semibold">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {history.rows.map((x, i) => (
                  <tr key={i} className="border-b border-border/60">
                    <td className="whitespace-nowrap px-2 py-1.5">{String(x.at || "").slice(0, 16).replace("T", " ")}</td>
                    <td className="px-2 py-1.5">{x.by || "—"}</td>
                    <td className="px-2 py-1.5">{x.action}</td>
                    <td className="max-w-[200px] truncate px-2 py-1.5">{x.entity_name || "—"}</td>
                    <td className="px-2 py-1.5">{x.field}</td>
                    <td className="max-w-[140px] truncate px-2 py-1.5 text-muted-foreground">{x.old || "—"}</td>
                    <td className="max-w-[140px] truncate px-2 py-1.5 font-medium">{x.new || "—"}</td>
                  </tr>
                ))}
                {!history.rows.length && (
                  <tr><td colSpan={7} className="px-2 py-3 text-muted-foreground">
                    Kilitten sonra veri değişikliği yapılmadı.
                  </td></tr>
                )}
              </tbody>
            </table>
          </ScrollArea>
        )}
      </Card>

      {noteFor && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={() => setNoteFor(null)} data-testid="note-modal">
          <Card className="w-full max-w-lg p-4" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-sm font-bold">Sapma nedeni / not</h3>
            <p className="mt-0.5 text-xs text-muted-foreground">{noteFor.company_name}</p>
            <div className="mt-3 flex flex-wrap gap-1.5">
              {(data.reasons || []).map((rs) => (
                <button key={rs} type="button" onClick={() => setNoteDraft({ ...noteDraft, reason: rs })}
                  data-testid={`note-reason-${rs}`}
                  className={`rounded-full px-2.5 py-1 text-xs font-semibold ${
                    noteDraft.reason === rs ? "bg-[#0F2C59] text-white" : "bg-muted text-muted-foreground"}`}>
                  {rs}
                </button>
              ))}
            </div>
            <textarea value={noteDraft.note} onChange={(e) => setNoteDraft({ ...noteDraft, note: e.target.value })}
              rows={3} placeholder="Açıklama (opsiyonel)" data-testid="note-text"
              className="mt-3 w-full rounded-md border border-border bg-background p-2 text-sm" />
            {noteFor.note_history?.length > 0 && (
              <div className="mt-3 max-h-32 overflow-y-auto rounded border border-border bg-muted/40 p-2 text-[11px]"
                data-testid="note-history">
                <b className="text-xs">Tarihçe</b>
                {noteFor.note_history.slice().reverse().map((h, i) => (
                  <div key={i} className="mt-1 border-b border-border/50 pb-1 last:border-0">
                    {String(h.at || "").slice(0, 16).replace("T", " ")} · <b>{h.by}</b>:
                    {" "}«{`${h.old_reason || ""} ${h.old_note || ""}`.trim() || "boş"}» → «
                    {`${h.new_reason || ""} ${h.new_note || ""}`.trim() || "boş"}»
                  </div>
                ))}
              </div>
            )}
            <div className="mt-3 flex justify-end gap-2">
              <Button size="sm" variant="ghost" onClick={() => setNoteFor(null)} data-testid="note-cancel">
                Vazgeç
              </Button>
              <Button size="sm" onClick={saveNote} disabled={busy} data-testid="note-save">
                <Save className="mr-1.5 h-4 w-4" /> Kaydet
              </Button>
            </div>
          </Card>
        </div>
      )}

      <Card className="p-4" data-testid="reconcile-heatmap">
        <div className="flex flex-wrap items-center gap-2">
          <h3 className="flex flex-1 items-center gap-1.5 text-sm font-bold">
            <Flame className="h-4 w-4 text-orange-600" /> Sapma ısı haritası — komite × neden
          </h3>
          <Button size="sm" variant="outline" disabled={busy}
            onClick={() => download("heatmap", "xlsx")} data-testid="export-heatmap-xlsx">
            <Download className="mr-1.5 h-4 w-4" /> Excel
          </Button>
          <Button size="sm" variant="outline" disabled={busy}
            onClick={() => download("heatmap", "pdf")} data-testid="export-heatmap-pdf">
            <Download className="mr-1.5 h-4 w-4" /> PDF
          </Button>
        </div>
        <p className="mt-0.5 text-xs text-muted-foreground">
          Hücredeki sayı, o komitede o nedenle kaybedilen/sapan firma sayısıdır. Renk koyulaştıkça
          kayıp artar. Otomatik kategoriler + girdiğin sapma notları birlikte değerlendirilir.
        </p>
        {hm?.top_reasons?.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1.5" data-testid="heatmap-top-reasons">
            {hm.top_reasons.map((t, i) => (
              <span key={t.category}
                className="rounded-full bg-orange-100 px-2.5 py-1 text-xs font-semibold text-orange-900 dark:bg-orange-950 dark:text-orange-300">
                {i + 1}. {t.category.replace("Not: ", "")} · {t.count}
              </span>
            ))}
          </div>
        )}
        {hm?.grand_total ? (
          <ScrollArea className="mt-3 h-[420px]">
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-muted">
                <tr className="text-left">
                  <th className="px-2 py-1.5 font-semibold">K</th>
                  <th className="px-2 py-1.5 font-semibold">Komite</th>
                  <th className="px-2 py-1.5 font-semibold">Top.</th>
                  {hm.categories.map((c) => (
                    <th key={c} className="px-1 py-1.5 text-center text-[10px] font-semibold leading-tight">
                      {c.replace("Not: ", "")}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {hm.rows.map((r) => (
                  <tr key={r.committee_no} className="border-b border-border/60"
                    data-testid={`heatmap-row-${r.committee_no}`}>
                    <td className="px-2 py-1 font-bold">{r.committee_no}</td>
                    <td className="max-w-[200px] truncate px-2 py-1">{r.name}</td>
                    <td className="px-2 py-1 font-bold">{r.total || ""}</td>
                    {hm.categories.map((c) => {
                      const v = r.cells[c] || 0;
                      const a = hm.max ? Math.min(1, v / hm.max) : 0;
                      return (
                        <td key={c} className="px-1 py-1 text-center font-semibold"
                          style={{
                            background: v ? `rgba(220,38,38,${0.12 + a * 0.75})` : "transparent",
                            color: a > 0.55 ? "#fff" : undefined,
                          }}>
                          {v || ""}
                        </td>
                      );
                    })}
                  </tr>
                ))}
                <tr className="bg-muted font-bold">
                  <td className="px-2 py-1.5" />
                  <td className="px-2 py-1.5">TOPLAM</td>
                  <td className="px-2 py-1.5">{hm.grand_total}</td>
                  {hm.categories.map((c) => (
                    <td key={c} className="px-1 py-1.5 text-center">{hm.totals[c] || ""}</td>
                  ))}
                </tr>
              </tbody>
            </table>
          </ScrollArea>
        ) : (
          <p className="mt-3 text-xs text-muted-foreground" data-testid="heatmap-empty">
            Henüz sapma verisi yok — çeteleye işaretleme yapıldıkça ve sapma notları girildikçe
            harita burada renklenir.
          </p>
        )}
      </Card>

      <Card className="p-4" data-testid="reconcile-archive">
        <div className="flex flex-wrap items-center gap-2">
          <h3 className="flex flex-1 items-center gap-1.5 text-sm font-bold">
            <Archive className="h-4 w-4" /> Seçim arşivi (2030 karşılaştırması için)
          </h3>
          <input value={archLabel} onChange={(e) => setArchLabel(e.target.value)}
            data-testid="archive-label"
            className="h-9 w-[160px] rounded-md border border-border bg-background px-2 text-xs" />
          <Button size="sm" disabled={busy} onClick={createArchive} data-testid="archive-create">
            <Archive className="mr-1.5 h-4 w-4" /> Arşivle (dondur)
          </Button>
        </div>
        <p className="mt-0.5 text-xs text-muted-foreground">
          Arşiv, o anki tüm muhakeme verisini (komiteler, firmalar, kişiler, sandık sonuçları,
          notlar, tarihçe, skorlar) dondurur. Arşivler <b>salt okunur</b>, sonradan değişmez.
        </p>
        <div className="mt-3 space-y-2">
          {!archives.length && (
            <p className="text-xs text-muted-foreground" data-testid="archive-empty">
              Henüz arşiv yok.
            </p>
          )}
          {archives.map((a) => (
            <div key={a.id} className="flex flex-wrap items-center gap-2 rounded-md border border-border p-2"
              data-testid={`archive-${a.id}`}>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-semibold">{a.label}</p>
                <p className="text-[11px] text-muted-foreground">
                  {String(a.created_at).slice(0, 16).replace("T", " ")} · {a.created_by} ·
                  {" "}{a.counts?.firms} firma · {a.counts?.persons} kişi ·
                  {" "}{a.counts?.notes} not · {a.counts?.history} değişiklik ·
                  {" "}tutarlılık %{a.summary?.score_forecast} · {(a.size / 1048576).toFixed(1)} MB
                </p>
              </div>
              <Button size="sm" variant="outline" disabled={busy}
                data-testid={`archive-compare-${a.id}`}
                onClick={() => openCompare(a.id)}>
                <GitCompare className="mr-1.5 h-4 w-4" /> Karşılaştır
              </Button>
              <Button size="sm" variant="outline" disabled={busy}
                data-testid={`archive-xlsx-${a.id}`}
                onClick={() => downloadUrl(`/election-day/archives/${a.id}/export`,
                  { format: "xlsx" }, `ARSIV_${a.label}.xlsx`)}>
                <Download className="mr-1.5 h-4 w-4" /> Excel
              </Button>
              <Button size="sm" variant="outline" disabled={busy}
                data-testid={`archive-pdf-${a.id}`}
                onClick={() => downloadUrl(`/election-day/archives/${a.id}/export`,
                  { format: "pdf" }, `ARSIV_${a.label}.pdf`)}>
                <Download className="mr-1.5 h-4 w-4" /> PDF
              </Button>
            </div>
          ))}
        </div>
      </Card>

      {compare && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-black/60 p-3"
          onClick={() => setCompare(null)} data-testid="compare-modal">
          <Card className="mx-auto max-w-6xl p-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="flex-1 text-sm font-bold">
                Arşiv karşılaştırma — «{compare.meta.label}» ↔ bugün
              </h3>
              <Button size="sm" variant="ghost" onClick={() => setCompare(null)}
                data-testid="compare-close">Kapat</Button>
            </div>
            <p className="mt-0.5 text-xs text-muted-foreground">
              Arşiv: {String(compare.meta.created_at).slice(0, 16).replace("T", " ")} ·
              {" "}{compare.meta.created_by} (salt okunur) — sol değerler arşivden, sağ değerler
              bugünkü veriden gelir.
            </p>

            <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              {[
                ["Tutarlılık %", compare.old_summary.score_forecast, compare.new_summary.score_forecast],
                ["Bize oy (gerçek)", compare.old_summary.actual_biz, compare.new_summary.actual_biz],
                ["2026 tahmini", compare.old_summary.forecast_biz, compare.new_summary.forecast_biz],
                ["FAAL firma", compare.old_summary.faal, compare.new_summary.faal],
              ].map(([label, o, n]) => (
                <div key={label} className="rounded-md border border-border p-3"
                  data-testid={`compare-stat-${label}`}>
                  <p className="text-[11px] font-semibold uppercase text-muted-foreground">{label}</p>
                  <p className="mt-1 font-display text-lg font-bold">
                    <span className="text-muted-foreground">{o}</span>
                    <span className="mx-1.5 text-xs">→</span>
                    <span>{n}</span>
                  </p>
                  <p className={`text-xs font-semibold ${n - o > 0 ? "text-emerald-600" : n - o < 0 ? "text-red-600" : "text-muted-foreground"}`}>
                    {n - o > 0 ? "+" : ""}{Math.round((n - o) * 10) / 10}
                  </p>
                </div>
              ))}
            </div>

            <div className="mt-4" style={{ height: Math.max(380, compare.rows.length * 22) }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={compare.rows.map((r) => ({
                  k: `K${r.committee_no}`, name: r.name,
                  arsiv: r.old_actual, bugun: r.new_actual,
                }))} layout="vertical" margin={{ left: 8, right: 24, top: 4, bottom: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                  <XAxis type="number" tick={{ fontSize: 11 }} />
                  <YAxis type="category" dataKey="k" width={44} tick={{ fontSize: 11 }} />
                  <Tooltip labelFormatter={(l) => compare.rows.find((r) => `K${r.committee_no}` === l)?.name || l} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar dataKey="arsiv" name="Arşiv (bize oy)" fill="#64748B" radius={[0, 3, 3, 0]} />
                  <Bar dataKey="bugun" name="Bugün (bize oy)" fill="#0F2C59" radius={[0, 3, 3, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>

            <ScrollArea className="mt-3 h-[300px]">
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-muted">
                  <tr className="text-left">
                    {["K", "Komite", "FAAL (arşiv→bugün)", "Tahmin (arşiv→bugün)",
                      "Gerçek (arşiv→bugün)", "Fark", "Tutarlılık (arşiv→bugün)"].map((h) => (
                      <th key={h} className="whitespace-nowrap px-2 py-1.5 font-semibold">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {compare.rows.map((r) => (
                    <tr key={r.committee_no} className="border-b border-border/60"
                      data-testid={`compare-row-${r.committee_no}`}>
                      <td className="px-2 py-1.5 font-bold">{r.committee_no}</td>
                      <td className="max-w-[200px] truncate px-2 py-1.5">{r.name}</td>
                      <td className="px-2 py-1.5">{r.old_faal ?? "—"} → {r.new_faal}</td>
                      <td className="px-2 py-1.5">{r.old_forecast ?? "—"} → {r.new_forecast}</td>
                      <td className="px-2 py-1.5">{r.old_actual ?? "—"} → {r.new_actual}</td>
                      <td className={`px-2 py-1.5 font-semibold ${r.delta_actual > 0 ? "text-emerald-600" : r.delta_actual < 0 ? "text-red-600" : "text-muted-foreground"}`}>
                        {r.delta_actual > 0 ? "+" : ""}{r.delta_actual}
                      </td>
                      <td className="px-2 py-1.5">%{r.old_score ?? "—"} → %{r.new_score}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </ScrollArea>
          </Card>
        </div>
      )}

      <Card className="p-4" data-testid="reconcile-loss-summary">
        <div className="flex flex-wrap items-center gap-2">
          <h3 className="flex flex-1 items-center gap-1.5 text-sm font-bold">
            <FileDown className="h-4 w-4" /> Kayıp nedeni özeti (komite başkanlarına)
          </h3>
          <select value={lossCno} onChange={(e) => setLossCno(e.target.value)}
            data-testid="loss-select"
            className="h-9 rounded-md border border-border bg-background px-2 text-xs">
            <option value="all">Tüm komiteler (her komite 1 sayfa)</option>
            {(coms.length ? coms : data.committees).map((c) => (
              <option key={c.committee_no} value={c.committee_no}>K{c.committee_no} · {c.name}</option>
            ))}
          </select>
          <Button size="sm" disabled={busy} data-testid="loss-pdf"
            onClick={() => downloadUrl("/election-day/reconcile/loss-summary",
              { format: "pdf", ...(lossCno === "all" ? {} : { cno: Number(lossCno) }) },
              `kayip_nedeni_ozeti_${lossCno}.pdf`)}>
            <Download className="mr-1.5 h-4 w-4" /> Tek Sayfalık Özet (PDF)
          </Button>
          <Button size="sm" variant="outline" disabled={busy} data-testid="chairman-file-pdf"
            onClick={() => downloadUrl("/election-day/reconcile/chairman-file",
              lossCno === "all" ? {} : { cno: Number(lossCno) },
              `baskan_dosyasi_${lossCno}.pdf`)}>
            <ClipboardList className="mr-1.5 h-4 w-4" /> Başkan Dosyası (karne + özet)
          </Button>
          <Button size="sm" variant="outline" disabled={busy} data-testid="loss-xlsx"
            onClick={() => downloadUrl("/election-day/reconcile/loss-summary",
              { format: "xlsx" }, "kayip_nedeni_ozeti.xlsx")}>
            <Download className="mr-1.5 h-4 w-4" /> Excel (48 komite)
          </Button>
        </div>
        <p className="mt-0.5 text-xs text-muted-foreground">
          Her komite için tek sayfa: katılım ve oy tablosu, <b>en çok oy kaybettiren 3 neden</b>
          {" "}(payı ve ne anlama geldiği) ve 1. nedenden etkilenen firmaların listesi.
        </p>
        <div className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
          {data.committees.slice(0, 6).map((c) => {
            const row = hm?.rows?.find((r) => r.committee_no === c.committee_no);
            const t = row ? Object.entries(row.cells).filter(([, v]) => v)
              .sort((a, b) => b[1] - a[1]).slice(0, 3) : [];
            return (
              <div key={c.committee_no} className="rounded-md border border-border p-2"
                data-testid={`loss-preview-${c.committee_no}`}>
                <p className="truncate text-xs font-bold">K{c.committee_no} · {c.name}</p>
                {t.length ? t.map(([k, v], i) => (
                  <p key={k} className="text-[11px] text-muted-foreground">
                    {i + 1}. {k.replace("Not: ", "")} — <b>{v}</b>
                  </p>
                )) : <p className="text-[11px] text-muted-foreground">kayıp kaydı yok</p>}
              </div>
            );
          })}
        </div>
      </Card>

      <Card className="p-4" data-testid="reconcile-explain">
        <h3 className="flex items-center gap-1.5 text-sm font-bold"><Target className="h-4 w-4" /> Rapor nasıl okunur?</h3>
        <ul className="mt-2 space-y-1.5 text-xs text-muted-foreground">
          <li><b>2026 Tahmini:</b> seçim öncesi saha verisinden hesaplanan bize oy beklentisi.</li>
          <li><b>Kötü Senaryo:</b> bize oy verecek olanların %10'u karşıya geçer, %10'u sandığa gelmez varsayımı.</li>
          <li><b>Çetele:</b> seçim günü sandıkta işaretlenen eğilimlerden bize sayılan oy.</li>
          <li><b>Sandık:</b> komite sonucuna girilen resmi oy sayısı — varsa gerçek referans budur.</li>
          <li><b>Tutarlılık %:</b> 100 − |tahmin − gerçek| / gerçek × 100. Gerçek yoksa çetele referans alınır.</li>
          <li><b>Şüpheli sapma:</b> tahminde bizden (KESİN/BELKİ) görünüp çetelede KARŞI işaretlenen firma.</li>
          <li><b>Oy gücü:</b> aynı kişinin birden fazla firmada oy kullanma sayısı — sapma varsa etkisi katlanır.</li>
        </ul>
      </Card>
    </div>
  );
}
